import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';

import './RaceSummary.scss';
import { RootState } from '../../../configs/store';
import RacingDetailContext from '../Context/RacingDetail.context';
import { getNumberOfCombinations, toRoman } from '../../../utils/utils';
import CombinationsModal from '../../NumbersBetting/LottoSummary/CombinationsModal/CombinationsModal';
import Icon from '../../Icon/Icon';
import { checkIfCrossEvent, combinationsValues, getNumbersToPredictCount, orderPosition } from '../../../utils/racingCombinations';
import Button from 'src/components/Button/Button';
import useIsMobile from 'src/hooks/useIsMobile';
import AppModeContext from 'src/containers/AppMode/AppMode.context';

function RaceSummary() {
  const {
    event:{
      eventStart,
      raceNo
    },
    event,
    selectedHorsePosition,
    selectedSelectionType,
    selectedMarket,
    combinations,
    setCombinations,
    setSelectedHorsePosition
  } = useContext(RacingDetailContext);

  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { slip } = useSelector((state: RootState) => state.betslip);
  const { meeting: { name, country, type } } = useSelector((state: RootState) => state.racing);
  const { authenticated } = useSelector((state: RootState) => state.auth);
  const activeWallet = useSelector((state: RootState) => state.auth.localWalletSelected);
  const [stake, setStake] = useState('1.00');
  const { RETAIL_MODE } = useContext(AppModeContext);

  const { t } = useTranslation();
  const mobile = useIsMobile();

  const [orderedPositions, setOrderedPositions] = useState({});
  const [showCombinations, setShowCombinations] = useState(false);
  const [showWarning, setShowWarning] = useState('');
  const [hasOverFlow, sethasOverFlow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const overFlow = useRef(null);

  const dispatch = useDispatch();

  const date = eventStart ? format(utcToZonedTime(eventStart * 1000, selectedTimezone), 'HH:mm') : '';

  useEffect(() => {
    setOrderedPositions(orderPosition(selectedSelectionType, selectedMarket, selectedHorsePosition));
  }, [selectedHorsePosition]);

  useEffect(() => {
    if (showWarning) {
      setTimeout(
        () => setShowWarning(''),
        3000
      );
    }
  }, [showWarning]);

  useEffect(() => {
    //Check if we show Expand Bar
    if ((mobile && overFlow.current?.clientWidth + overFlow.current.offsetLeft >= overFlow.current.parentElement.clientWidth) ||
    !mobile && overFlow.current?.clientWidth >= 223){
      sethasOverFlow(true);
    } else {
      sethasOverFlow(false);
    }
  }, [orderedPositions, expanded]);

  const checkIfNoBankers = () => {
    let hasBankers = false;
    for (const key of Object.keys(orderedPositions)){
      if (orderedPositions[key]?.length > 0 && key !=='all'){
        hasBankers = true;
        break;
      }
    }

    if (!hasBankers && orderedPositions['all']?.length >0){
      return true;
    }

    return false;
  };

  const handleCombinations = async (forceCall=false) => {
    if (checkIfNoBankers() && !forceCall){
      const { inOrder, numbersToPredictCount } = getNumbersToPredictCount(selectedMarket);

      const permutationsCount = getNumberOfCombinations(orderedPositions['all'], numbersToPredictCount, inOrder);
      setCombinations({ prediction: { permutationsCount, totalStake: +stake } });
    } else {
      const payload = {
        eventCode: event.eventCode,
        marketCode: selectedMarket.code,
        marketOutcomeCode: selectedMarket.outcomes[0].code,
        stake: +stake,
        line: 1,
        prediction: combinationsValues(orderedPositions, selectedSelectionType, selectedMarket, selectedHorsePosition),
        productType: 2
      };

      const response = async () => {
        const data = await dispatch.lotto.permutations(payload);
        setCombinations(data?.bets[0]);
      };
      if (selectedHorsePosition.length >= selectedMarket.predictionCount) {
        response();
      } else {
        setCombinations({});
      }
    }
  };

  useEffect(() => {
    handleCombinations();
  }, [orderedPositions, stake]);

  const addToBetSlip = async (placeBet = false) => {
    let sportIcon = 'horses';
    if (type === 8004) sportIcon = 'dogs';
    else if (type === 8003) sportIcon = 'carriages';

    const bet = {
      eventCode:event.eventCode,
      providerCode: 1,
      odds: 'TP',
      marketCode: selectedMarket.code,
      marketOutcomeCode: selectedMarket.outcomes[0].code,
      tournamentName: name,
      tournament: name,
      datetime: event.eventStart * 1000,
      eventName: name,
      sport: 'racing',
      outcome: checkIfCrossEvent(selectedMarket) ? '' : selectedSelectionType,
      type:'racing',
      ...(selectedMarket.isCrossEventMarket && { crossEvent: 'cross_event' }),
      orderedPosition: orderedPositions,
      permutationsCount: combinations.prediction.permutationsCount,
      prediction: combinationsValues(orderedPositions, selectedSelectionType, selectedMarket, selectedHorsePosition),
      marketName: selectedMarket.name.toLowerCase(),
      marketDetails: selectedMarket,
      country,
      stake,
      horsePosition: selectedHorsePosition,
      jsonLabel: orderedPositions,
      horseType: selectedMarket.oddsType === 2 ? 'TP' : 'FO',
      productType: 2,
      raceNo:  event.raceNo,
      selectionType: selectedSelectionType,
      repetitionsCount: checkIfCrossEvent(selectedMarket) ? 2 : 1,
      racingType: sportIcon
    };

    combinations.prediction.permutationsCount > 1 && await dispatch.betslip.changeStakeSelection('differentStake');
    if (!placeBet){
      await dispatch.betslip.addBet(bet);
    } else {
      await dispatch.betslip.placeBet({ isRetail: RETAIL_MODE, bet: [bet] });
    }


    const numberOfPositions = checkIfCrossEvent(selectedMarket) ? selectedMarket.predictionCount :
      selectedMarket.predictionCount +1;
    setSelectedHorsePosition([...Array(numberOfPositions)].map(() => Array(0)));
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    const { name } = e.target;
    const isComboBet = combinations.prediction.permutationsCount > 1 || checkIfCrossEvent(selectedMarket);

    if (_.isEmpty(combinations) || combinations?.prediction?.permutationsCount < 1 || (isComboBet && name === 'addBet' && slip !== 0)){
      let message = !checkIfCrossEvent(selectedMarket) ?
        t('racingDetail.pickAtLeast') :
        t('racingDetail.pickForEachLeg');

      if (isComboBet && !(_.isEmpty(combinations) || combinations?.prediction?.permutationsCount < 1)){
        message = t('racingDetail.addToBetslipError');
      }

      setShowWarning(message);
    } else if (name === 'quickBet' || !name) {
      if (authenticated || RETAIL_MODE){
        await addToBetSlip(true);
      } else {
        dispatch.content.modalType('#login-no-redirect');
      }
    } else {
      await addToBetSlip();
    }
  };

  const handleClose = (e) => {
    e.preventDefault();
    setShowWarning('');
  };

  const handleExpand = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  const convertToFixed = () => {
    const newStake = (+stake).toFixed(2);
    setStake(newStake.toString());
  };

  const handleCombinationsClick = async (e) => {
    e.preventDefault();
    await handleCombinations(true);
    setShowCombinations(!showCombinations);
  };

  const checkValue = () => {
    if (combinations && combinations.prediction?.totalStake > 0 && combinations.prediction?.permutationsCount >= 1){

      if (combinations.prediction['totalStake'] && combinations.prediction['stake']) {
        return combinations.prediction['totalStake'];
      }
      return Math.abs(combinations.prediction.totalStake * -(+(combinations.prediction.permutationsCount)));
    }

    return '-.--';
  };

  return (
    <>
      {
        !showWarning && (
          <>
            {
              (hasOverFlow || expanded) &&
              (
                <div className="race-summary__expand" onClick={ handleExpand } />
              )
            }
            <div className="race-summary__info">
              <div>{ raceNo && (`${ name } - ${ t('racingDetail.race') } ${ raceNo }, ${ date }`) }</div>
              { `${ selectedMarket.name.toLowerCase() } ${ selectedSelectionType ? t(`racingDetail.${ selectedSelectionType }`) : '' }` }
              <div
                className={
                  classNames({
                    'race-summary__numbers':true,
                    'race-summary__numbers--clamped': hasOverFlow && !expanded,
                    'race-summary__numbers--expanded': expanded
                  })
                }
                ref={ overFlow }
              >
                {
                  _.map(orderedPositions, (positions, key) => {
                    return (
                      <React.Fragment key={ key }>
                        <span
                          className={
                            classNames({
                              'race-summary__position': true,
                              'race-summary__position--hidden': false
                            })
                          }
                          key={ key }
                        >{ !isNaN(+key) ? toRoman(key) : key }:{
                            _.map(positions, (horseNumber, index) =>  {
                              return (<span key={ index } className="race-summary__competitor-number">{ horseNumber }</span>);
                            })
                          }
                        </span>
                      </React.Fragment>
                    );
                  })
                }
              </div>
              {
                combinations?.prediction?.permutationsCount > 0 && <div
                  className="race-summary__combinations"
                  onClick={ handleCombinationsClick }
                >
                  { t('numbers.combinations') } { combinations.prediction.permutationsCount }
                </div>
              }
            </div>
            <div className ={
              classNames({
                'race-summary__button': true,
                'race-summary__button--margin': hasOverFlow && !mobile,
                'race-summary__button--padding': combinations?.prediction?.permutationsCount > 0
              })
            }
            >
              <div className="race-summary__stake-container">
                <span className="race-summary__stake-label">{ t('numbers.stakePerBet') }</span>
                <input
                  type="text"
                  autoComplete="new-password"
                  value={ stake }
                  onChange={ (e) => setStake(e.target.value) }
                  onBlur={ convertToFixed }
                  className="lotto-summary__stake"
                />
              </div>
              <div className="race-summary__buttons-container">
                <button
                  type="button"
                  name="quickBet"
                  className={
                    classNames({
                      'race-summary__quick-bet' : true,
                      'race-summary__quick-bet--disabled': _.isEmpty(combinations) || combinations?.prediction?.permutationsCount < 1,
                      'btn btn--cta': true
                    })
                  }
                  onClick={ handleSubmitClick }
                >
                  {
                    (!activeWallet || activeWallet.typeCode !== 81 || _.isEmpty(activeWallet)) &&
                    `${ t('numbers.quickBet') } - ${ checkValue() }`
                  }
                  {
                    (activeWallet && activeWallet.type === 'Bonus') &&
                      <div className="race-summary__bonus-container">
                        <span>Quick</span>
                        <div className="race-summary__bonus-badge">Bonus Bet</div>
                        <span>
                          { checkValue() }
                        </span>
                      </div>
                  }
                </button>
                <Button
                  type="button"
                  size="medium"
                  name="addBet"
                  className={
                    classNames({
                      'race-summary__submit' : true,
                      'race-summary__submit--disabled': _.isEmpty(combinations) || combinations?.prediction?.permutationsCount < 1 ||
                    combinations?.prediction?.permutationsCount > 1 && slip !== 0
                    })
                  }
                  onClick={ handleSubmitClick }
                  text={ t('numbers.addToBetslip') }
                />
              </div>
            </div>
          </>
        )
      }
      {
        showWarning && (
          <>
            <div className="race-summary__message">
              { showWarning }
            </div>
            <Icon icon="close" className="race-summary__close" onClick={ handleClose } />
          </>
        )
      }
      {
        showCombinations && (
          <>
            <CombinationsModal
              title={ t('numbers.combinations') }
              permutations={ combinations.permutations }
              odds={ combinations?.odds }
              permutations2={ combinations.prediction?.permutations2 }
              onClick={ () => setShowCombinations(!showCombinations) }
              profit={ combinations.prediction.minimumReturn }
              isRacing
            />
          </>
        )
      }
    </>
  );

}

export default RaceSummary;
